import { FunctionComponent } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useStore } from "@nanostores/preact";
import { useLocation } from "wouter-preact";
import { useTranslator } from "../../hooks/i18n.ts";
import { $cameraList, $cameraListLoaded, updateCameraInfo, refreshCameraPage } from "../../store/cameras.ts";
import { usePageRefresh } from "../../hooks/use_page_refresh.ts";
import { $isLogined, $isSubAccount } from "../../store/user_info.ts";
import { gotoLoginPage } from "../router.tsx";
import { navigate } from "../../router_utils.ts";
import { Icon } from "../../components/icon/icon.tsx";
import { PageHeader } from "../../components/ui/page_header.tsx";
import { DataGrid, DataGridColumn } from "../../components/ui/data_grid.tsx";
import { useDialog } from "../../components/dialog/dialog.tsx";
import { createPayment, getProducts, getPaymentMethods } from "../../api/payment.ts";
import type { CameraInfo } from "../../store/cameras.ts";
import type { SubscriptionProduct } from "../../api/payment.ts";import QRCode from "qrcode";

export const CamerasPage: FunctionComponent = (_) => {
    // Add build version log
    useEffect(() => {
        console.log("CamerasPage Mounted - Build Version: v1.0.2 (QRCode Config Fixed)");
    }, []);

    const t = useTranslator();
    const isLogined = useStore($isLogined);
    const isSubAccount = useStore($isSubAccount);
    const cameraList = useStore($cameraList);
    const cameraListLoaded = useStore($cameraListLoaded);
    const [location, _setLocation] = useLocation();
    const pageRefresh = usePageRefresh({ pageKey: "cameras", enabled: isLogined, refresh: refreshCameraPage });
    const isRefreshing = pageRefresh.refreshing;

    // Subscription Dialog State
    const [selectedCamera, setSelectedCamera] = useState<CameraInfo | null>(null);
    const [products, setProducts] = useState<SubscriptionProduct[]>([]);
    const [selectedProductId, setSelectedProductId] = useState<number>(0);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('alipay');
    const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
    
    // Fetch products on mount
    useEffect(() => {
        if (isLogined) {
            getProducts().then(res => {
                if (res.products && res.products.length > 0) {
                    const cameraProducts = res.products.filter((p) => p.product_type === "camera");
                    setProducts(cameraProducts);
                    setSelectedProductId(cameraProducts[0]?.id ?? 0);
                }
            }).catch(() => {});
            getPaymentMethods().then(res => {
                const methods = res.methods || [];
                setPaymentMethods(methods);
                if (methods.length > 0) {
                    setSelectedPaymentMethod(methods[0]);
                }
            }).catch(() => {});
        }
    }, [isLogined]);

    const subDialog = useDialog({
        title: t({ zh: "设备续费", en: "Renew Subscription" }),
        showCloseButton: true,
        children: (
            <div style={{ padding: "1rem" }}>
                {qrCodeDataUrl ? (
                    <div style={{ textAlign: "center" }}>
                        <p>{t({ zh: "请使用微信扫描二维码支付", en: "Please scan QR code with WeChat" })}</p>
                        <img src={qrCodeDataUrl} alt="Payment QR Code" style={{ width: "200px", height: "200px" }} />
                        <div style={{ marginTop: "1rem" }}>
                             <button onClick={() => {
                                 setQrCodeDataUrl('');
                                 subDialog.close();
                                 handleRefresh();
                             }}>
                                 {t({ zh: "完成支付", en: "Done" })}
                             </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <p>
                            {t({ zh: "为设备", en: "Device" })}: <strong>{selectedCamera?.camera_name}</strong>
                        </p>
                        {products.length > 0 ? (
                            <>
                                <label>
                                    {t({ zh: "选择套餐", en: "Select Plan" })}
                                    <select 
                                        value={selectedProductId} 
                                        onChange={(e) => {
                                            setSelectedProductId(parseInt(e.currentTarget.value));
                                        }}
                                    >
                                        {products.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name} - {p.currency === 'CNY' ? '¥' : '$'}{p.price} ({p.duration_days} {t({ zh: "天", en: "Days" })})
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {paymentMethods.length > 0 ? (
                                    <label>
                                        {t({ zh: "支付方式", en: "Payment Method" })}
                                        <select 
                                            value={selectedPaymentMethod} 
                                            onChange={(e) => setSelectedPaymentMethod(e.currentTarget.value)}
                                        >
                                            {paymentMethods.map((m) => (
                                                <option key={m} value={m}>
                                                    {m === "alipay" ? t({ zh: "支付宝", en: "Alipay" })
                                                        : m === "wechat" ? t({ zh: "微信支付", en: "WeChat Pay" })
                                                        : m === "paypal" ? t({ zh: "PayPal", en: "PayPal" })
                                                        : m}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                ) : (
                                    <p style={{ color: "red" }}>{t({ zh: "暂无可用支付方式，请联系管理员", en: "No payment methods available. Contact admin." })}</p>
                                )}
                                <p>
                                    {t({ zh: "描述", en: "Description" })}: {products.find(p => p.id === selectedProductId)?.description || "-"}
                                </p>
                            </>
                        ) : (
                            <p style={{ color: "red" }}>{t({ zh: "暂无可用套餐，请联系管理员", en: "No plans available, contact admin." })}</p>
                        )}
                    </>
                )}
            </div>
        ),
        onConfirm: async () => {
            if (qrCodeDataUrl) {
                // If showing QR code, the confirm button (if visible) should probably just close or be hidden
                // But we replaced content, so this logic might need adjustment if using default dialog buttons
                // For now, let's assume default "Confirm" button is still there. 
                // We can just close.
                setQrCodeDataUrl('');
                subDialog.close();
                return;
            }

            if (!selectedCamera || !selectedProductId) return;
            if (paymentMethods.length === 0) {
                alert(t({ zh: "暂无可用支付方式，请联系管理员", en: "No payment methods available. Contact admin." }));
                subDialog.close();
                return;
            }
            
            // Flag to track if we should close the dialog
            let shouldClose = false;
            
            try {
                // Check for WeChat code
                const searchParams = new URLSearchParams(window.location.search);
                const code = searchParams.get('code') || undefined;
                
                console.log("Creating Payment for:", selectedCamera.camera_id, selectedProductId, selectedPaymentMethod);
                const res = await createPayment(selectedCamera.camera_id, selectedProductId, selectedPaymentMethod, code);
                console.log("Payment Response:", res);
                
                // Handle OAuth requirement (WeChat)
                if (res.need_oauth && res.appid) {
                    const currentUrl = window.location.href;
                    // Ensure we don't stack codes
                    const urlObj = new URL(currentUrl);
                    urlObj.searchParams.delete('code');
                    urlObj.searchParams.delete('state');
                    const redirectUri = encodeURIComponent(urlObj.toString());
                    const appId = res.appid;
                    const oauthUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_base&state=STATE#wechat_redirect`;
                    window.location.href = oauthUrl;
                    return; // Redirecting, no need to close
                }
                
                // Handle WeChat JSAPI
                if (res.type === 'jsapi' && res.jsapi_params) {
                     const onBridgeReady = () => {
                        (window as any).WeixinJSBridge.invoke(
                            'getBrandWCPayRequest', res.jsapi_params,
                            function(res: any){
                                if(res.err_msg == "get_brand_wcpay_request:ok" ) {
                                    alert(t({ zh: "支付成功", en: "Payment Successful" }));
                                    handleRefresh();
                                }
                            }
                        );
                    };

                    if (typeof (window as any).WeixinJSBridge == "undefined"){
                        if( document.addEventListener ){
                            document.addEventListener('WeixinJSBridgeReady', onBridgeReady, false);
                        } else if ((document as any).attachEvent){
                            (document as any).attachEvent('WeixinJSBridgeReady', onBridgeReady); 
                            (document as any).attachEvent('onWeixinJSBridgeReady', onBridgeReady);
                        }
                    } else {
                        onBridgeReady();
                    }
                    shouldClose = true; // JSAPI handled, can close
                } else if (res.code === 200 && res.pay_url) {
                    console.log("Payment Created. Type:", res.type, "URL:", res.pay_url);

                    // Handle Native Pay (QR Code)
                    if (res.type === 'native') {
                        try {
                            const dataUrl = await QRCode.toDataURL(res.pay_url);
                            console.log("QR Code generated successfully");
                            setQrCodeDataUrl(dataUrl);
                            // IMPORTANT: Do NOT close dialog, show QR code
                            shouldClose = false;
                        } catch (err) {
                            console.error("QR Code generation failed", err);
                            alert(t({ zh: "生成二维码失败", en: "Failed to generate QR Code" }));
                            shouldClose = true; // Failed, close
                        }
                    } else {
                        // H5 or other Redirect
                        console.log("Redirecting to Payment:", res.pay_url);
                        
                        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                        
                        if (isMobile) {
                            window.location.href = res.pay_url;
                        } else {
                            // Desktop: Open in new tab to avoid potential blocking or SPA issues
                            const newWindow = window.open(res.pay_url, '_blank');
                            if (!newWindow) {
                                // If popup blocked, fallback to current window
                                window.location.href = res.pay_url;
                            }
                        }
                        shouldClose = true; // Redirecting, can close
                    }
                } else {
                    console.error("Payment creation failed:", res);
                    alert(t({ zh: "创建订单失败: " + (res.msg || "未知错误"), en: "Failed to create order: " + (res.msg || "Unknown error") }));
                    shouldClose = false; // Error shown, maybe keep open to retry? Or close. Let's keep open for now or close.
                    // Actually, if failed, user might want to try again. But alert blocks.
                    shouldClose = true;
                }
            } catch (e: any) {
                console.error("Payment request error:", e);
                alert(t({ zh: "支付请求错误: " + (e.message || "未知"), en: "Payment request error: " + (e.message || "Unknown") }));
                shouldClose = true;
            }

            if (shouldClose) {
                subDialog.close();
            }
        }
    });

    const openSubDialog = (cam: CameraInfo) => {
        setSelectedCamera(cam);
        setQrCodeDataUrl(''); // Reset QR code state
        if (products.length > 0 && selectedProductId === 0) {
            setSelectedProductId(products[0].id);
        }
        subDialog.open();
    };

    useEffect(() => {
        document.title = t({ zh: "相机管理", en: "Camera Management" });
    }, [t]);

    useEffect(() => {
        if (!isLogined) {
            gotoLoginPage(location ?? "/cameras");
        }
    }, [isLogined, location]);

    const handleRefresh = pageRefresh.refreshNow;

    const columns: DataGridColumn<CameraInfo>[] = [
        {
            key: "camera_id",
            title: "ID",
            width: "60px",
            align: "center"
        },
        {
            key: "camera_name",
            title: t({ zh: "名称", en: "Name" }),
            width: "45%",
            render: (cam) => (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Icon name="camera" size={16} />
                    <strong>{cam.camera_name}</strong>
                    {cam.camera_pinned_time >= 0 && <Icon name="paperclip" size={12} />}
                </div>
            )
        },
        {
            key: "group_name",
            title: t({ zh: "分组", en: "Group" }),
            width: "15%",
            render: (cam) => cam.group_name || "-"
        },
        {
            key: "status",
            title: t({ zh: "状态", en: "Status" }),
            width: "120px",
            render: (cam) => {
                const isOnline = (Date.now() / 1000 - cam.last_photo_time) < 3600;
                return (
                    <span class={`farpov_badge ${isOnline ? "success" : "neutral"}`}>
                        {isOnline ? t({ zh: "在线", en: "Online" }) : t({ zh: "离线", en: "Offline" })}
                    </span>
                );
            }
        },
        {
            key: "photo_count",
            title: t({ zh: "照片数", en: "Pic" }),
            width: "100px",
            align: "right",
            render: (cam) => cam.photo_count.toLocaleString()
        },
        {
            key: "subscription",
            title: t({ zh: "订阅", en: "Sub" }),
            width: "120px",
            render: (cam) => {
                if (cam.oss_type === "own") {
                    return (
                        <span 
                            class="farpov_badge success" 
                            title={t({ zh: "您正在使用自有存储，无需订阅", en: "Using own storage, no subscription needed" })}
                        >
                            {t({ zh: "自有存储", en: "Own Storage" })}
                        </span>
                    );
                }
                if (cam.oss_type === "none") {
                    return (
                        <span 
                            class="farpov_badge neutral" 
                            title={t({ zh: "未绑定云存储，请先设置", en: "No cloud storage bound, please set up" })}
                        >
                            {t({ zh: "无云存储", en: "No Storage" })}
                        </span>
                    );
                }
                if (!cam.subscription_expire) {
                    return (
                        <span 
                            class="farpov_badge error" 
                            title={t({ zh: "未订阅，请续费", en: "Not subscribed, please renew" })}
                        >
                            {t({ zh: "未订阅", en: "Not Subscribed" })}
                        </span>
                    );
                }
                const now = Date.now() / 1000;
                const daysLeft = Math.ceil((cam.subscription_expire - now) / 86400);
                const isExpired = daysLeft <= 0;
                return (
                    <span 
                        class={`farpov_badge ${isExpired ? "error" : (daysLeft < 7 ? "warning" : "success")}`}
                        title={new Date(cam.subscription_expire * 1000).toLocaleDateString()}
                    >
                        {isExpired ? t({ zh: "已过期", en: "Expired" }) : `${daysLeft} ${t({ zh: "天", en: "Days" })}`}
                    </span>
                );
            }
        },
        {
            key: "actions",
            title: t({ zh: "操作", en: "Actions" }),
            align: "right",
            width: "200px",
            render: (cam) => (
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                    {!isSubAccount && (
                        <button 
                            class="outline contrast" 
                            style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem", width: "auto", marginBottom: 0 }}
                            onClick={() => openSubDialog(cam)}
                            title={t({ zh: "续费", en: "Renew" })}
                        >
                            <Icon name="credit-card" size={14} />
                        </button>
                    )}
                    <button 
                        class="outline secondary" 
                        style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem", width: "auto", marginBottom: 0 }}
                        onClick={() => navigate(`/camera_setting/${cam.camera_id}`, { replace: false })}
                        title={t({ zh: "设置", en: "Settings" })}
                    >
                        <Icon name="settings" size={14} />
                    </button>
                    {!isSubAccount && (
                        <button 
                            class="outline primary" 
                            style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem", width: "auto", marginBottom: 0 }}
                            onClick={() => navigate(`/camera/${cam.camera_id}`, { replace: false })}
                            title={t({ zh: "编辑", en: "Edit" })}
                        >
                            <Icon name="edit" size={14} />
                        </button>
                    )}
                    <button 
                        class="outline" 
                        style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem", width: "auto", marginBottom: 0 }}
                        onClick={() => navigate(`/photos/${cam.camera_id}`, { replace: false })}
                        title={t({ zh: "照片", en: "Pic" })}
                    >
                        <Icon name="image" size={14} />
                    </button>
                </div>
            )
        }
    ];

    if (!isLogined) {
        return <article aria-busy="true" class="container"></article>;
    }

    return (
        <div class="farpov_top_container">
            <PageHeader 
                title={t({ zh: "相机管理", en: "Cameras" })} 
                subtitle={t({ zh: "管理您的相机设备和配置", en: "Manage your camera devices and configurations" })}
                actions={
                    <>
                        <button class="outline" disabled={isRefreshing} onClick={handleRefresh}>
                            <Icon name="refresh-cw" /> {t({ zh: "刷新", en: "Refresh" })}
                        </button>
                        {!isSubAccount && (
                            <button onClick={() => navigate("/camera/new", { replace: false })}>
                                <Icon name="plus" /> {t({ zh: "新增相机", en: "New Camera" })}
                            </button>
                        )}
                    </>
                }
            />
            
            {pageRefresh.error && <p role="alert">{pageRefresh.error}</p>}
            <DataGrid
                columns={columns} 
                data={cameraList} 
                keyField="camera_id"
                loading={!cameraListLoaded && isRefreshing}
                emptyText={t({ zh: "暂无相机", en: "No cameras found" })}
                class="farpov_cameras_grid"
            />
        </div>
    );
};

export default CamerasPage;
